import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { fetchMe, listUsers, setUserActive } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { useAuth } from "@/hooks/useAuth";
import { formatDate, formatDateTime } from "@/lib/format";
import type { AdminUser } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

export function UsersAdmin(): JSX.Element {
  usePageTitle("Users");
  const auth = useAuth();
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["admin-users"], queryFn: () => listUsers() });
  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe });

  const [toggleError, setToggleError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) => setUserActive(id, next),
    onSuccess: () => {
      setToggleError(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) =>
      setToggleError(e instanceof ApiError ? e.message : "Change failed — try again."),
  });

  function onToggle(u: AdminUser): void {
    const verb = u.is_active ? "Deactivate" : "Reactivate";
    // eslint-disable-next-line no-alert
    if (window.confirm(`${verb} ${u.email}? ${u.is_active ? "They will be signed out everywhere immediately." : "They will be able to sign in again."}`)) {
      toggle.mutate({ id: u.id, next: !u.is_active });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-ink-300">
          Need to add someone?{" "}
          <Link to="/admin/invitations" className="underline hover:text-ink-100">
            Send an invitation
          </Link>
          .
        </p>
      </header>

      <section className="card">
        <h2 className="card-title">
          {auth.scope === "platform" ? "All users" : "Your team"}
          <InfoTip
            label="Who appears here"
            body="Everyone with an account, including deactivated users. Deactivating revokes access immediately but keeps the account and its history; invitations that haven't been accepted yet live on the Invitations page instead."
          />
        </h2>
        {toggleError && (
          <p className="text-sm text-severity-high mb-2" role="alert">
            {toggleError}
          </p>
        )}
        {users.isLoading ? (
          <div className="skeleton h-32" aria-label="Loading users" />
        ) : users.isError ? (
          <p className="text-sm text-severity-high">
            {users.error instanceof ApiError ? users.error.message : "Failed to load"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink-300 border-b border-ink-700">
                <tr>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Scope / Role</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">
                    Last sign-in
                    <InfoTip
                      label="Last sign-in"
                      body="The most recent successful sign-in recorded in the activity log. Blank for users who haven't signed in since activity logging began."
                    />
                  </th>
                  <th className="py-2 pr-4 font-medium">Member since</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700">
                {(users.data ?? []).map((u) => (
                  <tr key={u.id} className={u.is_active ? "" : "opacity-60"}>
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4 text-ink-300">
                      {u.scope} / {u.role}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={u.is_active ? "pill-ok" : "pill-high"}>
                        {u.is_active ? "active" : "deactivated"}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-ink-300">
                      {u.last_login ? formatDateTime(u.last_login) : "—"}
                    </td>
                    <td className="py-2 pr-4 text-ink-300">{formatDate(u.created_at)}</td>
                    <td className="py-2">
                      {me.data?.id === u.id ? (
                        <span className="text-ink-400 text-xs" title="You cannot deactivate yourself">
                          you
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onToggle(u)}
                          disabled={toggle.isPending}
                          className="btn-primary py-1 px-2 text-xs"
                        >
                          {u.is_active ? "Deactivate" : "Reactivate"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
